"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils/cn";
import { useOverlayPosition, type OverlayPlacement } from "./use-overlay-position";

export type PopoverProps = {
  trigger: ReactNode;
  children: ReactNode | ((close: () => void) => ReactNode);
  placement?: OverlayPlacement;
  className?: string;
  contentClassName?: string;
  ariaLabel?: string;
  disabled?: boolean;
};

export function Popover({ trigger, children, placement = "bottom-start", className, contentClassName, ariaLabel, disabled }: PopoverProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const position = useOverlayPosition(open, rootRef, placement, "content", 8, contentRef);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !contentRef.current?.contains(target)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
    }
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  return (
    <span ref={rootRef} className={cn("relative inline-flex", className)}>
      <button type="button" className="inline-flex rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 disabled:cursor-not-allowed" aria-haspopup="dialog" aria-expanded={open} disabled={disabled} onClick={() => setOpen((value) => !value)}>
        {trigger}
      </button>
      {open && position && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={contentRef}
              style={position}
              role="dialog"
              aria-label={ariaLabel}
              className={cn("max-w-sm overflow-y-auto overscroll-contain rounded-card border border-card-border bg-surface-elevated p-ds-4 text-body text-foreground shadow-overlay", contentClassName)}
            >
              {typeof children === "function" ? children(close) : children}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
